import React, { useState, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { fetchUserAttributes } from '@aws-amplify/auth';
import { getUserSettings } from '../utils/api';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import { isEmulating, getOriginalUserSettings } from '../utils/emulation';
import LoadingScreen from './LoadingScreen';

function AdminProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { userSettings } = useUserSettingsContext();

  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      // First check if user is authenticated
      await fetchUserAttributes();
      setIsAuthenticated(true);

      // Check if we're emulating - if so, use the original admin's settings for access control
      const emulating = isEmulating();
      const originalSettings = emulating ? getOriginalUserSettings() : null;

      // Then check if user is an admin
      if (emulating && originalSettings) {
        // When emulating, always use the original admin's settings for admin access check
        setIsAdmin(originalSettings.isAdmin === 1);
        setIsLoading(false);
      } else if (userSettings) {
        // If userSettings is already loaded from context
        setIsAdmin(userSettings.isAdmin === 1);
        setIsLoading(false);
      } else {
        // Otherwise fetch user settings directly
        const attributes = await fetchUserAttributes();
        const email = attributes.email;

        if (email) {
          const settings = await getUserSettings(email);
          setIsAdmin(settings && settings.isAdmin === 1);
        }
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error checking authentication:', error);
      setIsAuthenticated(false);
      setIsAdmin(false);
      setIsLoading(false);
    }
  }

  if (isLoading) {
    return <LoadingScreen message="Connecting" />;
  }

  if (!isAuthenticated) {
    // Redirect to login if not authenticated
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    // Redirect to dashboard if authenticated but not an admin
    return <Navigate to="/dashboard" replace />;
  }

  // If authenticated and admin, render the children or Outlet for nested routes
  return children ? children : <Outlet />;
}

export default AdminProtectedRoute; 